import { useState, useMemo, useCallback, useRef, useEffect, memo, type CSSProperties } from 'react';
import { Stack, Group, Text, Button, ActionIcon, Badge, Box, Menu } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconGripVertical,
  IconX,
  IconPlus,
  IconDots,
  IconArrowBarToUp,
  IconArrowBarToDown,
} from '@tabler/icons-react';
import {
  DndContext,
  closestCenter,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useVirtualizer } from '@tanstack/react-virtual';
import { usePlaylistMedia } from '../usePlaylistMedia';
import { PlaylistUtils } from '../PlaylistUtils';
import { PLAYLIST_VIRTUALIZER_OVERSCAN, type PlaylistAddPosition } from '@/lib/constants/playlist';
import { MediaPickerModal } from './MediaPickerModal';
import type { PlaylistMediaPosition } from '@/lib/types/Playlist';
import type { MediaType } from '@/lib/types/Media';

// Fixed row height keeps the virtualizer measurement-free (rows are text-only).
const ITEM_ROW_HEIGHT = 44;
// Bounded scroll container: playlists reach 1000+ items, so the list gets its
// own scroll region instead of stretching the page (keeps the save bar near).
const ITEMS_MAX_HEIGHT = '65vh';

interface PlaylistItemsManagerProps {
  mediaPositions: PlaylistMediaPosition[];
  onChange: (positions: PlaylistMediaPosition[]) => void;
  mediaType: MediaType;
}

interface ItemMediaInfo {
  title: string;
  isActive: boolean;
}

export function PlaylistItemsManager({ mediaPositions, onChange, mediaType }: PlaylistItemsManagerProps) {
  const { items } = usePlaylistMedia(mediaType);
  const noun = PlaylistUtils.getMediaNoun(mediaType);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [pickerOpened, { open: openPicker, close: closePicker }] = useDisclosure(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [pendingScrollIndex, setPendingScrollIndex] = useState<number | null>(null);

  // Build lookup map for item titles from cached data
  const mediaMap = useMemo(() => {
    const map = new Map<number, ItemMediaInfo>();
    for (const { media } of items) {
      map.set(media.mediaId, {
        title: media.title,
        isActive: media.mediaStateType === 'active',
      });
    }
    return map;
  }, [items]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  // IDs for SortableContext (must be strings or numbers)
  const sortableIds = useMemo(
    () => mediaPositions.map(p => p.mediaId),
    [mediaPositions]
  );

  const excludeMediaIds = useMemo(
    () => mediaPositions.map(p => p.mediaId),
    [mediaPositions]
  );

  // Virtualized rows: only the visible window mounts, so 1000+ item playlists
  // stay responsive. SortableContext still receives ALL ids — dnd-kit supports
  // virtualized sortable lists as long as the DragOverlay renders the active item.
  const rowVirtualizer = useVirtualizer({
    count: mediaPositions.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ITEM_ROW_HEIGHT,
    overscan: PLAYLIST_VIRTUALIZER_OVERSCAN,
    getItemKey: (index) => mediaPositions[index].mediaId,
  });

  // Scroll to newly added items after the list re-renders with the new count.
  useEffect(() => {
    if (pendingScrollIndex === null) return;
    rowVirtualizer.scrollToIndex(pendingScrollIndex);
    setPendingScrollIndex(null);
  }, [pendingScrollIndex, rowVirtualizer]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as number);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = mediaPositions.findIndex(p => p.mediaId === active.id);
    const newIndex = mediaPositions.findIndex(p => p.mediaId === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(mediaPositions, oldIndex, newIndex);
    // Recalculate positions (0-based sequential)
    const updated = reordered.map((p, i) => ({ ...p, position: i }));
    onChange(updated);
  }, [mediaPositions, onChange]);

  const handleRemove = useCallback((mediaId: number) => {
    const filtered = mediaPositions
      .filter(p => p.mediaId !== mediaId)
      .map((p, i) => ({ ...p, position: i }));
    onChange(filtered);
  }, [mediaPositions, onChange]);

  // Long-distance moves without dragging across hundreds of rows.
  const moveToIndex = useCallback((mediaId: number, toIndex: number) => {
    const fromIndex = mediaPositions.findIndex(p => p.mediaId === mediaId);
    if (fromIndex === -1 || fromIndex === toIndex) return;
    const updated = arrayMove(mediaPositions, fromIndex, toIndex).map((p, i) => ({ ...p, position: i }));
    onChange(updated);
  }, [mediaPositions, onChange]);

  const handleMoveToTop = useCallback((mediaId: number) => {
    moveToIndex(mediaId, 0);
    setPendingScrollIndex(0);
  }, [moveToIndex]);

  const handleMoveToBottom = useCallback((mediaId: number) => {
    moveToIndex(mediaId, mediaPositions.length - 1);
    setPendingScrollIndex(mediaPositions.length - 1);
  }, [moveToIndex, mediaPositions.length]);

  const handleAddVideos = useCallback((mediaIds: number[], addPosition: PlaylistAddPosition) => {
    const added: PlaylistMediaPosition[] = mediaIds.map(mediaId => ({ mediaId, position: 0 }));
    const combined = addPosition === 'beginning'
      ? [...added, ...mediaPositions]
      : [...mediaPositions, ...added];
    onChange(combined.map((p, i) => ({ ...p, position: i })));
    setPendingScrollIndex(addPosition === 'beginning' ? 0 : mediaPositions.length);
  }, [mediaPositions, onChange]);

  function getItemTitle(mediaId: number): string {
    return mediaMap.get(mediaId)?.title ?? `Unknown ${noun.singular} (ID: ${mediaId})`;
  }

  const activeItem = activeId != null
    ? mediaPositions.find(p => p.mediaId === activeId)
    : null;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="xs">
          <Text fw={600} size="md">Playlist Items</Text>
          <Badge size="sm" variant="light">{mediaPositions.length}</Badge>
        </Group>
        <Button
          variant="light"
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={openPicker}
        >
          Add {noun.pluralTitle}
        </Button>
      </Group>

      {mediaPositions.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          No items in this playlist. Add {noun.plural} to get started.
        </Text>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            <div
              ref={scrollContainerRef}
              style={{ maxHeight: ITEMS_MAX_HEIGHT, overflowY: 'auto' }}
            >
              <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}>
                {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                  const pos = mediaPositions[virtualItem.index];
                  return (
                    <SortableItem
                      key={virtualItem.key}
                      mediaId={pos.mediaId}
                      position={virtualItem.index + 1}
                      title={getItemTitle(pos.mediaId)}
                      top={virtualItem.start}
                      onRemove={handleRemove}
                      onMoveToTop={handleMoveToTop}
                      onMoveToBottom={handleMoveToBottom}
                    />
                  );
                })}
              </div>
            </div>
          </SortableContext>
          <DragOverlay>
            {activeItem && (
              <ItemDisplay
                position={mediaPositions.findIndex(p => p.mediaId === activeItem.mediaId) + 1}
                title={getItemTitle(activeItem.mediaId)}
                isDragging
              />
            )}
          </DragOverlay>
        </DndContext>
      )}

      <MediaPickerModal
        opened={pickerOpened}
        onClose={closePicker}
        onAdd={handleAddVideos}
        excludeMediaIds={excludeMediaIds}
        mediaType={mediaType}
      />
    </Stack>
  );
}

interface SortableItemProps {
  mediaId: number;
  position: number;
  title: string;
  /** Virtualizer offset within the scroll container. */
  top: number;
  onRemove: (mediaId: number) => void;
  onMoveToTop: (mediaId: number) => void;
  onMoveToBottom: (mediaId: number) => void;
}

/**
 * Sortable item wrapper using @dnd-kit/sortable.
 * Uses setActivatorNodeRef so only the grip handle initiates drag.
 * Memoized — during a drag only rows with dnd-kit transform changes re-render,
 * not the whole (virtual) list.
 */
const SortableItem = memo(function SortableItem({
  mediaId,
  position,
  title,
  top,
  onRemove,
  onMoveToTop,
  onMoveToBottom,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: mediaId });

  // Virtualizer positions via `top`; dnd-kit animates via `transform` — the
  // two must not share the transform channel or sorting animations break.
  const style: CSSProperties = {
    position: 'absolute',
    top,
    left: 0,
    width: '100%',
    height: ITEM_ROW_HEIGHT,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Group
        gap="sm"
        wrap="nowrap"
        px="sm"
        h="100%"
        style={{
          borderBottom: '1px solid var(--mantine-color-gray-2)',
        }}
      >
        <ActionIcon
          ref={setActivatorNodeRef}
          {...listeners}
          variant="subtle"
          color="gray"
          style={{ cursor: 'grab', touchAction: 'none' }}
          aria-label="Drag to reorder"
        >
          <IconGripVertical size={16} />
        </ActionIcon>
        <Text size="xs" c="dimmed" w={30} ta="right">#{position}</Text>
        <Text size="sm" style={{ flex: 1 }} truncate="end">{title}</Text>
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray" aria-label="Item actions">
              <IconDots size={14} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconArrowBarToUp size={14} />}
              onClick={() => onMoveToTop(mediaId)}
            >
              Move to top
            </Menu.Item>
            <Menu.Item
              leftSection={<IconArrowBarToDown size={14} />}
              onClick={() => onMoveToBottom(mediaId)}
            >
              Move to bottom
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={() => onRemove(mediaId)}
          aria-label="Remove item"
        >
          <IconX size={14} />
        </ActionIcon>
      </Group>
    </div>
  );
});

/** Static display for DragOverlay (renders styled copy at cursor) */
function ItemDisplay({
  position,
  title,
  isDragging,
}: {
  position: number;
  title: string;
  isDragging?: boolean;
}) {
  return (
    <Box
      px="sm"
      py="xs"
      bg="white"
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 'var(--mantine-radius-sm)',
        boxShadow: isDragging ? 'var(--mantine-shadow-md)' : undefined,
      }}
    >
      <Group gap="sm" wrap="nowrap">
        <IconGripVertical size={16} color="var(--mantine-color-gray-5)" />
        <Text size="xs" c="dimmed" w={30} ta="right">#{position}</Text>
        <Text size="sm" truncate="end">{title}</Text>
      </Group>
    </Box>
  );
}
